import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Loader2, Mail, Phone, Globe, Users, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/** Platform-admin page: review beta waitlist entries. Only visible to
 *  super-admin accounts (backend gates on `is_super_admin`). */
export default function AdminAccessRequests() {
  const [state, setState] = useState({ loading: true, items: [], counts: {} });
  const [filter, setFilter] = useState("pending"); // pending | approved | declined | all
  const [decideFor, setDecideFor] = useState(null); // {req, action}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/access-requests${q}`);
      setState({ loading: false, items: data.items || [], counts: data.counts || {} });
    } catch (e) {
      setState({ loading: false, items: [], counts: {},
                   error: e.response?.data?.detail || "Failed to load" });
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const decide = async () => {
    if (!decideFor) return;
    setBusy(true);
    try {
      if (decideFor.action === "approve") {
        const { data } = await api.post(`/admin/access-requests/${decideFor.req._id}/approve`);
        toast.success(`Approved — invite sent (expires 72h). Workspace: ${data.business_id.slice(-6)}`);
      } else {
        await api.post(`/admin/access-requests/${decideFor.req._id}/decline`, { reason: reason || null });
        toast.success("Request declined");
      }
      setDecideFor(null); setReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const industryLabel = (v) => (v || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  if (state.loading) {
    return <div className="p-6 flex items-center gap-2 text-zinc-500" data-testid="access-requests-loading">
      <Loader2 className="w-4 h-4 animate-spin"/> Loading…
    </div>;
  }
  if (state.error) {
    return <div className="p-6 max-w-xl">
      <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp">
        <div className="flex items-center gap-2 text-red-700"><XCircle className="w-5 h-5"/> <b>{state.error}</b></div>
      </div>
    </div>;
  }

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-5xl" data-testid="access-requests-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold" data-testid="access-requests-heading">Access requests</h1>
          <p className="text-sm text-zinc-500">LeadForge is in private beta — approve to create a workspace + send invite.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <ShieldCheck className="w-4 h-4 text-emerald-600"/> Platform admin only
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="access-requests-filters">
        {[["pending","Pending"],["approved","Approved"],["declined","Declined"],["all","All"]].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`ar-filter-${k}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === k ? "bg-zinc-900 text-white border-zinc-900"
                                : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
            }`}>
            {l}{state.counts[k] != null && k !== "all" ? ` (${state.counts[k]})` : ""}
          </button>
        ))}
      </div>

      {state.items.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-12 text-center text-zinc-500" data-testid="ar-empty">
          No {filter === "all" ? "" : filter} requests.
        </div>
      ) : (
        <div className="space-y-3">
          {state.items.map((r) => (
            <div key={r._id} className="bg-white border border-zinc-200 rounded-xl p-5 card-crisp" data-testid={`ar-card-${r._id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold text-lg">{r.full_name}</div>
                    <span className="px-2 py-0.5 rounded-full bg-orange-100 text-[#E04F00] text-xs font-medium">
                      {industryLabel(r.industry)}
                    </span>
                    {r.status === "pending" && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs">Pending</span>}
                    {r.status === "approved" && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs">Approved</span>}
                    {r.status === "declined" && <span className="px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 text-xs">Declined</span>}
                  </div>
                  <div className="text-sm text-zinc-700 font-medium mt-0.5">{r.business_name}</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm text-zinc-600">
                    <div className="inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5"/>{r.email}</div>
                    <div className="inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5"/>{r.phone}</div>
                    {r.website && <div className="inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5"/><a href={r.website} target="_blank" rel="noreferrer" className="hover:underline">{r.website}</a></div>}
                    <div className="inline-flex items-center gap-2"><Users className="w-3.5 h-3.5"/>{r.employees} employee{r.employees==="1"?"":"s"}</div>
                    <div className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5"/>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                  </div>
                  {r.needs && (
                    <div className="mt-3 pl-3 border-l-2 border-orange-200 text-sm text-zinc-700 whitespace-pre-wrap">
                      <div className="text-[10px] uppercase text-[#E04F00] tracking-widest font-mono-accent mb-1">What they need</div>
                      {r.needs}
                    </div>
                  )}
                  {r.decline_reason && (
                    <div className="mt-2 text-xs text-zinc-500 italic">Decline reason: {r.decline_reason}</div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                                onClick={() => setDecideFor({ req: r, action: "decline" })}
                                data-testid={`ar-decline-${r._id}`}>
                      <XCircle className="w-4 h-4 mr-1"/> Decline
                    </Button>
                    <Button className="bg-emerald-700 hover:bg-emerald-800 text-white"
                                onClick={() => setDecideFor({ req: r, action: "approve" })}
                                data-testid={`ar-approve-${r._id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1"/> Approve
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decideFor} onOpenChange={(o) => { if (!o) { setDecideFor(null); setReason(""); } }}>
        <DialogContent data-testid="ar-decide-dialog">
          <DialogHeader>
            <DialogTitle>
              {decideFor?.action === "approve" ? "Approve request" : "Decline request"}
            </DialogTitle>
          </DialogHeader>
          {decideFor?.action === "approve" ? (
            <div className="text-sm text-zinc-700 space-y-2">
              <p>This creates a new workspace for <b>{decideFor.req.business_name}</b> and emails <b>{decideFor.req.email}</b> a 72-hour set-password link.</p>
              <p className="text-xs text-zinc-500">No user is created in LeadForge until they accept the invite.</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-zinc-700 mb-2">Mark this request declined. Reason is private (not emailed).</p>
              <Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)}
                            placeholder="Optional — e.g. ‘not a service business’"
                            data-testid="ar-decline-reason"/>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDecideFor(null); setReason(""); }}>Cancel</Button>
            <Button disabled={busy} onClick={decide} data-testid="ar-confirm-btn"
                        className={decideFor?.action === "approve" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-red-700 hover:bg-red-800 text-white"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (decideFor?.action === "approve" ? "Approve & send invite" : "Decline request")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
